// src/services/moodBasedPDFGenerator.ts
// Complete rewrite - renders actual usable activities for each type
import jsPDF from 'jspdf'
import LexendDecaVF from '../assets/fonts/LexendDeca-VF.ttf?url'
import type { WorksheetData } from '../types/phonicsWorksheet'

export async function generateMoodBasedPDF(
  data: WorksheetData,
  filename?: string
): Promise<void> {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4')
    // Load variable font asynchronously
    const response = await fetch(LexendDecaVF)
    const fontBlob = await response.blob()
    const fontBase64 = await new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1]
        resolve(base64)
      }
      reader.readAsDataURL(fontBlob)
    })

    pdf.addFileToVFS('LexendDeca-VF.ttf', fontBase64)
    pdf.addFont('LexendDeca-VF.ttf', 'LexendDeca', 'normal', 400)
    pdf.addFont('LexendDeca-VF.ttf', 'LexendDeca', 'bold', 700)
    pdf.setFont('LexendDeca', 'normal')
    const pageWidth = 210
    const pageHeight = 297

    // Mood colors
    const moodColors: Record<string, [number, number, number]> = {
      overwhelmed: [148, 187, 233], // Blue accent
      high_energy: [255, 193, 7],   // Yellow accent
      steady: [156, 39, 176]         // Purple accent
    }
    
    // ========== ACTIVITY PAGE (single activity, clean layout) ==========
    const activity = data.activities[0]
    const [r, g, b] = moodColors[data.config.mood]
    let currentY = 30

    // Title
    pdf.setFont('LexendDeca', 'normal')  // Changed from 'bold'
    pdf.setFontSize(32)  // Increased from 28
    pdf.setTextColor(50, 50, 50)  // Lighter black (not pure black)
    pdf.text(activity.name, pageWidth / 2, currentY, { align: 'center' })
    currentY += 12

    // Thin colored line
    pdf.setDrawColor(r, g, b)
    pdf.setLineWidth(1.5)
    pdf.line(30, currentY, pageWidth - 30, currentY)
    currentY += 15

    // Instruction
    pdf.setFont('LexendDeca', 'normal')
    pdf.setFontSize(13)
    pdf.setTextColor(100, 100, 100)
    const instructions = getActivityInstructions(activity.type, activity.words)
    const instructionLines = pdf.splitTextToSize(instructions, pageWidth - 60)
    instructionLines.forEach((line: string) => {
      pdf.text(line, pageWidth / 2, currentY, { align: 'center' })
      currentY += 7
    })
    currentY += 25

    // Render activity content
    currentY = renderActivityContent(pdf, activity, currentY, data.config.mood)
    // ADD THIS LINE TO PREVENT EXTRA CONTENT:
    const activityEndY = currentY

    // Completion message at bottom
    currentY = pageHeight - 40
    pdf.setFont('LexendDeca', 'normal')
    pdf.setFontSize(11)
    pdf.setTextColor(120, 120, 120)
    const completionMsg = getCompletionMessage(activity.type)
    pdf.text(completionMsg, pageWidth / 2, currentY, { align: 'center' })

    // Thin colored line at bottom
    currentY += 5
    pdf.setDrawColor(r, g, b)
    pdf.setLineWidth(1.5)
    pdf.line(30, currentY, pageWidth - 30, currentY)

    // ========== PARENT NOTES PAGE (last page) ==========
    pdf.addPage()
    currentY = 25

    // Header
    pdf.setFont('LexendDeca', 'bold')
    pdf.setFontSize(20)
    pdf.setTextColor(0, 0, 0)
    pdf.text('Parent Notes', pageWidth / 2, currentY, { align: 'center' })
    currentY += 12

    // Thin line
    pdf.setDrawColor(r, g, b)
    pdf.setLineWidth(1.5)
    pdf.line(30, currentY, pageWidth - 30, currentY)
    currentY += 15

    // Why section
    pdf.setFont('LexendDeca', 'bold')
    pdf.setFontSize(13)
    pdf.text('Why these words?', 20, currentY)
    currentY += 8
    pdf.setFont('LexendDeca', 'normal')
    pdf.setFontSize(11)
    const whyLines = pdf.splitTextToSize(data.parentNotes.why, pageWidth - 40)
    pdf.text(whyLines, 20, currentY)
    currentY += whyLines.length * 6 + 12

    // How section
    pdf.setFont('LexendDeca', 'bold')
    pdf.setFontSize(13)
    pdf.text('Why this activity?', 20, currentY)
    currentY += 8
    pdf.setFont('LexendDeca', 'normal')
    pdf.setFontSize(11)
    const howLines = pdf.splitTextToSize(data.parentNotes.how, pageWidth - 40)
    pdf.text(howLines, 20, currentY)
    currentY += howLines.length * 6 + 12

    // Expectations section
    pdf.setFont('LexendDeca', 'bold')
    pdf.setFontSize(13)
    pdf.text('What success looks like:', 20, currentY)
    currentY += 8
    pdf.setFont('LexendDeca', 'normal')
    pdf.setFontSize(11)
    const expectLines = pdf.splitTextToSize(data.parentNotes.expectations, pageWidth - 40)
    pdf.text(expectLines, 20, currentY)
    currentY += expectLines.length * 6 + 12

    // Time estimate
    pdf.setFont('LexendDeca', 'bold')
    pdf.setFontSize(12)
    pdf.text(`Estimated time: ${data.estimatedTime} minutes`, 20, currentY)
    
    // Download
    const finalFilename = filename || `${data.config.pattern}_${data.config.mood}_${Date.now()}.pdf`
    pdf.save(finalFilename)
    
  } catch (error) {
    console.error('Error generating mood-based PDF:', error)
    throw new Error('Failed to generate worksheet PDF')
  }
}

function getCompletionMessage(activityType: string): string {
  switch (activityType) {
    case 'circle_match':
      return '5 matches found. That\'s enough for today.'
    case 'connect_pairs':
      return '4 connections made. That\'s the task done.'
    case 'find_your_word':
      return 'Word found. Good focus today.'
    case 'trace_one':
      return 'One word traced is one word practiced.'
    default:
      return 'Task complete. Well done.'
  }
}

// ========== ACTIVITY INSTRUCTIONS ==========
function getActivityInstructions(activityType: string, words: string[]): string {
  switch (activityType) {
    case 'trace_one':
      return 'Pick ONE word from below to trace 3 times on a separate paper. Just one is perfect.'
    
    case 'circle_match':
      const targetWord = words[0]
      return `Find the word "${targetWord}" in each row. Circle it when you see it.`
    
    case 'connect_pairs':
      return 'Draw lines to connect words that are exactly the same.'
    
    case 'find_your_word':
      return `Circle the word "${words[0]}" every time you see it.`
    
    case 'rhyme_time':
      return `Which words rhyme with "${words[0]}"? Circle the rhyming words.`
    
    case 'sound_sort':
      return 'Sort these words by their vowel sound. Put words with similar sounds together.'
    
    case 'word_hunt':
      return 'Find and circle all the words from your list hidden in the grid below.'
    
    default:
      return 'Complete this activity at your own pace.'
  }
}

// ========== ACTIVITY CONTENT RENDERING ==========
function renderActivityContent(
  pdf: jsPDF,
  activity: any,
  startY: number,
  mood: string
): number {
  let currentY = startY
  const fontSize = mood === 'overwhelmed' ? 20 : 16
  
  switch (activity.type) {
    case 'trace_one':
      currentY = renderTraceActivity(pdf, activity.words, currentY, fontSize)
      break
    
    case 'circle_match':
      currentY = renderCircleMatchActivity(pdf, activity.words, currentY, fontSize)
      break
    
    case 'connect_pairs':
      currentY = renderConnectPairsActivity(pdf, activity.words, currentY, fontSize)
      break
    
    case 'find_your_word':
      currentY = renderFindWordActivity(pdf, activity.words, currentY, fontSize)
      break
    
    default:
      // Simple word list fallback
      currentY = renderSimpleWordList(pdf, activity.words, currentY, fontSize)
      break
  }
  
  return currentY
}

// ========== TRACE ACTIVITY ==========
function renderTraceActivity(pdf: jsPDF, words: string[], startY: number, fontSize: number): number {
  let currentY = startY
  
  pdf.setFont('LexendDeca', 'normal')
  pdf.setFontSize(fontSize + 8)
  pdf.setTextColor(220, 220, 220)
  
  words.slice(0, 3).forEach((word, index) => {
    const x = 30 + (index % 2) * 80
    const y = currentY + Math.floor(index / 2) * 45
    pdf.text(word, x, y)
  })
  
  currentY += Math.ceil(words.length / 2) * 45 + 20
  
  pdf.setFontSize(14)
  pdf.setTextColor(0, 0, 0)
  pdf.text('I traced _________________________', 30, currentY)
  
  return currentY + 20
}

// ========== CIRCLE MATCH ACTIVITY ==========
function renderCircleMatchActivity(pdf: jsPDF, words: string[], startY: number, fontSize: number): number {
  let currentY = startY
  const targetWord = words[0]
  const distractors = words.slice(1, 4)
  
  // Show target word in orange accent
  pdf.setFont('LexendDeca', 'bold')
  pdf.setFontSize(fontSize + 4)
  pdf.setTextColor(255, 140, 0)
  pdf.text(targetWord, 40, currentY)
  currentY += 30
  
  // Create 5 rows with target + distractors
  pdf.setTextColor(0, 0, 0)
  pdf.setFont('LexendDeca', 'normal')
  pdf.setFontSize(fontSize)
  
  for (let i = 0; i < 5; i++) {
    const rowWords = [
      distractors[i % distractors.length],
      targetWord,
      distractors[(i + 1) % distractors.length]
    ].sort(() => Math.random() - 0.5)
    
    pdf.text(rowWords.join('   '), 50, currentY)
    currentY += fontSize * 2
  }
  
  return currentY + 20
}

// ========== CONNECT PAIRS ACTIVITY ==========
function renderConnectPairsActivity(pdf: jsPDF, words: string[], startY: number, fontSize: number): number {
  let currentY = startY
  const pairCount = Math.min(words.length, 5) // Use actual word count
  const leftWords = words.slice(0, pairCount)
  const rightWords = [...leftWords].sort(() => Math.random() - 0.5) // This already shuffles
  
  // SAFETY CHECK: Ensure right column is actually different from left
  let attempts = 0
  while (attempts < 10 && leftWords.every((word, i) => word === rightWords[i])) {
    rightWords.sort(() => Math.random() - 0.5)
    attempts++
  }
  
  pdf.setFont('LexendDeca', 'bold')
  pdf.setFontSize(fontSize)
  pdf.setTextColor(0, 0, 0)
  
  leftWords.forEach((word, i) => {
    pdf.text(word, 40, currentY)
    pdf.text(rightWords[i], 130, currentY)
    currentY += fontSize * 2.5
  })
  
  return currentY + 20
}

// ========== FIND WORD ACTIVITY ==========
function renderFindWordActivity(pdf: jsPDF, words: string[], startY: number, fontSize: number): number {
  let currentY = startY
  const targetWord = words[0]
  const distractors = words.slice(1)
  
  pdf.setFont('LexendDeca', 'normal')
  pdf.setFontSize(fontSize)
  pdf.setTextColor(0, 0, 0)
  
  for (let i = 0; i < 4; i++) {
    const line = [
      distractors[i % distractors.length],
      targetWord,
      distractors[(i + 1) % distractors.length],
      distractors[(i + 2) % distractors.length]
    ].sort(() => Math.random() - 0.5).join('   ')
    
    pdf.text(line, 30, currentY)
    currentY += fontSize * 2
  }
  
  return currentY + 20
}

// ========== SIMPLE WORD LIST FALLBACK ==========
function renderSimpleWordList(pdf: jsPDF, words: string[], startY: number, fontSize: number): number {
  let currentY = startY
  
  pdf.setFont('LexendDeca', 'normal')
  pdf.setFontSize(fontSize)
  
  const wordsPerRow = 2
  const boxWidth = 85
  const boxHeight = 35
  const spacing = 10
  
  words.forEach((word, index) => {
    const col = index % wordsPerRow
    const row = Math.floor(index / wordsPerRow)
    
    const x = 20 + col * (boxWidth + spacing)
    const y = currentY + row * (boxHeight + spacing)
    
    pdf.setDrawColor(150)
    pdf.rect(x, y, boxWidth, boxHeight)
    pdf.setTextColor(0, 0, 0)
    pdf.text(word, x + boxWidth / 2, y + boxHeight / 2, { align: 'center', baseline: 'middle' })
  })
  
  const totalRows = Math.ceil(words.length / wordsPerRow)
  return currentY + totalRows * (boxHeight + spacing) + 20
}